import { supabase } from "@/integrations/supabase/client";

/**
 * Thrown by `requireLiveSession` when the tab no longer holds a usable session.
 * React Query keeps the query's previous data and does not retry it (see
 * `shouldRetryQuery`), and the SIGNED_OUT event that follows clears `user`.
 */
export class NoLiveSessionError extends Error {
  constructor() {
    super("No live session; the authenticated-only request was not sent.");
    this.name = "NoLiveSessionError";
  }
}

/**
 * Call immediately before an authenticated-only request (an RPC or table that
 * grants nothing to `anon`). Throws `NoLiveSessionError` instead of letting the
 * request go out without a user token.
 *
 * WHY (#794). When a tab that was signed in wakes after its access token
 * expired, React Query refetches every stale query at once. Each request waits
 * on supabase-js's token refresh; if that refresh fails (refresh token revoked,
 * already used by another tab, or past its lifetime), supabase-js drops the
 * session and sends the requests with the PUBLISHABLE key. Postgres sees role
 * `anon`, the authenticated-only objects raise 42501, and PostgREST answers
 * 401: a burst of denied calls from an account that was signed in a moment
 * earlier. `user` in React state is still set at that point, so gating on
 * `enabled: !!user` does not prevent it.
 *
 * `getSession()` waits on the same in-flight refresh and returns null when it
 * failed, so checking it here closes that window. It does not add a network
 * call when the session is live: getSession reads local storage and refreshes
 * only an expired token, which the request itself would have done anyway.
 */
export async function requireLiveSession(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  if (!data?.session?.access_token) throw new NoLiveSessionError();
}

/** Default React Query retry policy: never retry a query skipped for lack of a session. */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof NoLiveSessionError) return false;
  return failureCount < 3; // React Query's own default
}
